import { useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, isVideo } from "@/api";
import { ImageUpload } from "@/components/admin/ImageUpload";

const empty = { image: "", title: "", artist_id: "", category: "tattoo" };

export const PortfolioTab = ({ portfolio, artists, refresh }) => {
  const [form, setForm] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    if (!form.image) { toast.error("Envie uma imagem ou vídeo"); return; }
    try {
      const payload = { ...form, artist_id: form.artist_id || null };
      if (form.id) await api.put(`/portfolio/${form.id}`, payload);
      else await api.post("/portfolio", { ...payload, order: portfolio.length });
      toast.success("Foto salva");
      setForm(null);
      refresh();
    } catch { toast.error("Erro ao salvar"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta foto?")) return;
    await api.delete(`/portfolio/${id}`);
    toast.success("Foto removida");
    refresh();
  };

  return (
    <div data-testid="admin-portfolio-tab">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-display uppercase text-3xl tracking-wider">Portfólio ({portfolio.length})</h2>
        <button onClick={() => setForm({ ...empty })} data-testid="portfolio-add-btn"
          className="inline-flex items-center gap-2 bg-white text-black font-mono2 text-[10px] tracking-[0.2em] uppercase px-4 py-2 hover:bg-[#E63946] hover:text-white transition-colors">
          <Plus size={14} /> Adicionar foto
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="border border-white/15 p-6 mb-8 grid gap-4 bg-[#121214]" data-testid="portfolio-form">
          <ImageUpload value={form.image} onChange={(p) => setForm({ ...form, image: p })} testid="portfolio-image" accept="image/*,video/*" kind="imagem ou vídeo" />
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Título (opcional)"
            data-testid="portfolio-title-input" className="bg-transparent border border-white/15 px-3 py-2 text-sm outline-none focus:border-white" />
          <div className="grid sm:grid-cols-2 gap-4">
            <select value={form.artist_id || ""} onChange={(e) => setForm({ ...form, artist_id: e.target.value })} data-testid="portfolio-artist-select"
              className="bg-[#121214] border border-white/15 px-3 py-2 text-sm outline-none focus:border-white">
              <option value="">Sem artista</option>
              {artists.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="portfolio-category-select"
              className="bg-[#121214] border border-white/15 px-3 py-2 text-sm outline-none focus:border-white">
              <option value="tattoo">Tatuagem (portfólio)</option>
              <option value="space">Espaço (seção O Espaço)</option>
            </select>
          </div>
          <div className="flex gap-3">
            <button type="submit" data-testid="portfolio-save-btn"
              className="bg-white text-black font-mono2 text-[10px] tracking-[0.2em] uppercase px-6 py-3 hover:bg-[#E63946] hover:text-white transition-colors">Salvar</button>
            <button type="button" onClick={() => setForm(null)} data-testid="portfolio-cancel-btn"
              className="border border-white/20 font-mono2 text-[10px] tracking-[0.2em] uppercase px-6 py-3 hover:border-white transition-colors">Cancelar</button>
          </div>
        </form>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {portfolio.map((p) => (
          <div key={p.id} className="group relative border border-white/10" data-testid={`portfolio-row-${p.id}`}>
            {isVideo(p.image) ? (
              <video src={fileUrl(p.image)} muted className="w-full aspect-square object-cover" />
            ) : (
              <img src={fileUrl(p.image)} alt={p.title} className="w-full aspect-square object-cover img-bw" />
            )}
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 p-2 bg-gradient-to-t from-black/85 to-transparent">
              <span className="font-mono2 text-[9px] tracking-[0.15em] uppercase text-zinc-300 truncate">{p.title || p.category}</span>
              <span className="flex gap-2 shrink-0">
                <button onClick={() => setForm({ ...empty, ...p, artist_id: p.artist_id || "" })} data-testid={`portfolio-edit-${p.id}`} aria-label="editar"
                  className="text-zinc-400 hover:text-white transition-colors"><Pencil size={14} /></button>
                <button onClick={() => remove(p.id)} data-testid={`portfolio-delete-${p.id}`} aria-label="remover"
                  className="text-zinc-400 hover:text-[#E63946] transition-colors"><Trash2 size={14} /></button>
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
